import { Divider, Grid, Typography } from '@mui/material';
import React, { FC } from 'react';
import IndicatorTile from '../IndicatorTile/IndicatorTile';
import { BodyComposition } from '../../../services/DataFormatService';
import BmiImage from '../../../assets/bodyComposition/bmi.svg';
import BriImage from '../../../assets/bodyComposition/bri.svg';
import BodySurfaceAreaImage from '../../../assets/bodyComposition/body-surface-area.svg';
import WaistCalfRatioImage from '../../../assets/bodyComposition/waist-calf-ratio.svg';
import WaistHeightRatioImage from '../../../assets/bodyComposition/waist-height-ratio.svg';
import WaistHipRatioImage from '../../../assets/bodyComposition/waist-hip-ratio.svg';
import WaistThighRatioImage from '../../../assets/bodyComposition/waist-thigh-ratio.svg';
import boddVariables from '../../../variables.module.scss';
import PopoverComponent from '../../PopoverComponent/PopoverComponent';

const DistributionIndicatorsInfo = () => (
    <div>
        <div>
            <Typography variant="h3">
                Waist-to-hip ratio
            </Typography>
            <Typography>
                A measurement used to assess the distribution of body fat and evaluate health risks associated with
                obesity.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Waist-to-height ratio
            </Typography>
            <Typography>
                A measure used to assess an individual&apos;s risk of obesity-related health issues by evaluating the
                distribution of body fat in relation to their height.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Waist-to-thigh ratio
            </Typography>
            <Typography>
                Used to assess the distribution of body fat, particularly comparing the fat stored in the abdominal
                region (waist) to that stored in the thighs.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Waist-to-calf ratio
            </Typography>
            <Typography>
                Compares the circumference of the waist to that of the calf. Like other body composition ratios, it is
                used to assess the distribution of body fat, particularly focusing on abdominal fat (waist) relative to
                peripheral fat (calf).
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                BMI
            </Typography>
            <Typography>
                Body Mass Index is used as an indicator of whether an individual has a healthy body weight for their
                height and to screen for potential health risks associated with being underweight, overweight, or obese.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                BRI
            </Typography>
            <Typography>
                Body Roundness Index is a valuable tool for evaluating body shape and central obesity, providing better
                insights into health risks related to fat distribution than traditional weight-based metrics.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Body Surface Area
            </Typography>
            <Typography>
                Body surface area (BSA) is the measured or calculated surface area of a human body, used to indicate
                metabolic mass.
            </Typography>
        </div>
    </div>
);

interface IndicatorsComponentProps {
    bodyComposition: BodyComposition;
}

const IndicatorsComponent: FC<IndicatorsComponentProps> = (props) => {
    const { bodyComposition } = props;
    return (
        <div data-testid="IndicatorsComponent">
            <Typography
                variant="h2"
                component="h2"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <PopoverComponent>
                    <DistributionIndicatorsInfo />
                </PopoverComponent>
                Fat/Mass Distribution Indicators
            </Typography>
            <Divider sx={{ borderColor: boddVariables.black, mb: 4, mt: 2 }} />
            <Grid
                container
                columnSpacing={2}
                rowSpacing={3}
                sx={{
                    alignItems: 'stretch',
                }}
            >
                {bodyComposition.waistToHip && (
                    <IndicatorTile
                        label="Waist-to-hip ratio"
                        value={bodyComposition.waistToHip!.toFixed(1)}
                        image={<WaistHipRatioImage />}
                    />
                )}
                {bodyComposition.waistToHeight && (
                    <IndicatorTile
                        label="Waist-to-height ratio"
                        value={bodyComposition.waistToHeight!.toFixed(1)}
                        image={<WaistHeightRatioImage />}
                    />
                )}
                {bodyComposition.waistToThigh && (
                    <IndicatorTile
                        label="Waist-to-thigh ratio"
                        value={bodyComposition.waistToThigh!.toFixed(1)}
                        image={<WaistThighRatioImage />}
                    />
                )}
                {bodyComposition.waistToCalf && (
                    <IndicatorTile
                        label="Waist-to-calf ratio"
                        value={bodyComposition.waistToCalf!.toFixed(1)}
                        image={<WaistCalfRatioImage />}
                    />
                )}
                {bodyComposition.bodyMassIndex && (
                    <IndicatorTile
                        label="BMI"
                        value={bodyComposition.bodyMassIndex!.toFixed(1)}
                        image={<BmiImage />}
                    />
                )}
                {bodyComposition.bodyRoundnessIndex && (
                    <IndicatorTile
                        label="BRI"
                        value={bodyComposition.bodyRoundnessIndex!.toFixed(1)}
                        image={<BriImage />}
                    />
                )}
                {bodyComposition.bodySurfaceArea && (
                    <IndicatorTile
                        label="Body Surface Area"
                        value={bodyComposition.bodySurfaceArea!.toFixed(1)}
                        image={<BodySurfaceAreaImage />}
                    />
                )}
            </Grid>
        </div>
    );
};

export default IndicatorsComponent;
